/**
 * New console — the lock screen.
 *
 * Blanks the console at a shared front desk without signing out: whoever comes back
 * types the account password and carries on, and the patient names that were on screen
 * are not readable by everyone walking past in between.
 *
 * What it is, precisely, so nobody leans on more than it does. The console stops
 * rendering — the screens behind are unmounted, not merely covered — and asks for the
 * password before starting again. The session on this device stays open, which is the
 * whole point of locking rather than signing out. So it is protection from the person
 * at the counter, not from somebody with the machine, the time, and developer tools.
 * For that there is Sign out, which is on this screen for exactly that reason.
 *
 * The password is checked by `/verify-password/`, the endpoint the classic console
 * already uses before a sensitive change, so the answer comes from the server rather
 * than from anything in this file that could be edited in a debugger.
 *
 * Locked state is kept in localStorage, so reloading the page — the first thing anyone
 * tries — comes back to this screen rather than to the console.
 */
import { useEffect, useRef, useState } from 'react';

import useAuth from '@hooks/useAuth';
import { verifyPasswordApi } from '@services/auth';

import useSignOut from '../useSignOut';
import { Button, initialsOf } from './Primitives';
import { IconLock, IconLogout } from './Icons';

/**
 * Locks after `minutes` with nothing touched. 0 — the default — never locks.
 *
 * A timer on its own misses the case the setting is actually for: the lid closed at a
 * front desk. A background tab has its timers throttled and a sleeping machine runs
 * none at all, so the elapsed time is measured again whenever the page comes back.
 */
export const useIdleLock = (minutes, locked, onLock) => {
  const fire = useRef(onLock);
  fire.current = onLock;

  useEffect(() => {
    const ms = Number(minutes) * 60000;
    if (!ms || locked) return undefined;

    let timer = 0;
    let last = Date.now();
    const arm = () => {
      last = Date.now();
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fire.current(), ms);
    };
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - last >= ms) fire.current();
      else arm();
    };

    const EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    EVENTS.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    arm();

    return () => {
      window.clearTimeout(timer);
      EVENTS.forEach((e) => window.removeEventListener(e, arm));
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [minutes, locked]);
};

const LockScreen = ({ onUnlock }) => {
  const { user } = useAuth();
  const signOut = useSignOut();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const name = `${(user && user.first_name) || ''} ${(user && user.last_name) || ''}`.trim();
  const initials = initialsOf(name, 'U');

  const unlock = async (e) => {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    const res = await verifyPasswordApi({ password });
    setBusy(false);
    if (!res.isOk) {
      /* Cleared, because the common case is a typo rather than the wrong person. */
      setPassword('');
      setError('That password does not match this account.');
      return;
    }
    setPassword('');
    setError('');
    onUnlock();
  };

  return (
    <div className="nt-lock" role="dialog" aria-modal="true" aria-label="The console is locked">
      <form className="nt-lock-card" onSubmit={unlock}>
        <span className="nt-lock-mark"><IconLock /></span>
        <h1>Locked</h1>

        <div className="nt-lock-who">
          <span className="nt-avatar">{initials}</span>
          <span className="txt">
            <b>{name || (user && user.email) || 'Signed in'}</b>
            <span>{(user && user.email) || ''}</span>
          </span>
        </div>

        <div className="nt-field">
          <label htmlFor="nt-lock-pw">Your password</label>
          <input
            id="nt-lock-pw"
            className="nt-input"
            type="password"
            value={password}
            autoComplete="current-password"
            /* The one screen where taking the caret is right: it is the only thing
               on it, and it is in the way of everything the person came back to do. */
            autoFocus
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
          />
          {error ? <div className="nt-hint bad">{error}</div> : null}
        </div>

        <Button type="submit" variant="primary" disabled={busy || !password}>
          {busy ? 'Checking…' : 'Unlock'}
        </Button>

        <p className="nt-lock-note">
          The console is blanked, not signed out — this device stays signed in, so unlocking
          puts you back where you were.
        </p>

        <Button type="button" className="nt-lock-out" onClick={signOut}>
          <IconLogout />Sign out instead
        </Button>
      </form>
    </div>
  );
};

export default LockScreen;
